import { createContext, useContext, useState, useEffect } from "react";
import { onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase";

export const PROMO_DEFAULTS = {
  active: false,
  bannerText: "Book 4 hours, pay for 3! Select 4+ consecutive slots to unlock the offer",
  minSlots: 4,
  type: "free_slots",
  value: 1,
};

// Human-readable label for each promo type value field
export const VALUE_LABELS = {
  free_slots:  "Number of free slots",
  percentage:  "Discount % (e.g. 10 for 10%)",
  flat:        "Amount off in ₹",
};

export const PROMO_TYPES = [
  { id: "free_slots",  label: "Free Slots"          },
  { id: "percentage",  label: "Percentage Discount"  },
  { id: "flat",        label: "Flat Amount Off"       },
];

const PromoContext = createContext(null);

export function PromoProvider({ children }) {
  const [promo, setPromo] = useState(PROMO_DEFAULTS);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "promotions"), (snap) => {
      if (snap.exists()) setPromo({ ...PROMO_DEFAULTS, ...snap.data() });
    });
    return () => unsub();
  }, []);

  /**
   * Returns the discount amount in ₹.
   * @param {number} rawTotal  - total before discount (slotsCount × rate)
   * @param {number} slotsCount - number of slots selected
   * @param {number} rate       - per-slot rate (used for free_slots type)
   */
  const calcDiscount = (rawTotal, slotsCount, rate) => {
    if (!promo.active || slotsCount < Number(promo.minSlots)) return 0;
    const val = Number(promo.value);
    switch (promo.type) {
      case "free_slots": {
        const freeSlots = Math.floor(slotsCount / Number(promo.minSlots)) * val;
        return Math.min(freeSlots * rate, rawTotal);
      }
      case "percentage":
        return Math.round(rawTotal * val / 100);
      case "flat":
        return Math.min(val, rawTotal);
      default:
        return 0;
    }
  };

  return (
    <PromoContext.Provider value={{ promo, calcDiscount }}>
      {children}
    </PromoContext.Provider>
  );
}

export function usePromo() {
  return useContext(PromoContext);
}
